import React from 'react';
import { Rss, Plus, RefreshCw, Filter, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const ThreatFeeds = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing threat feeds...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing feeds..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Rss className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Threat Feeds</h1>
            <p className="text-sm text-gray-400">Manage and monitor threat intelligence feeds</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Feed
          </Button>
        </div>
      </div>

      {/* Feed Categories */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Active Feeds', value: '12', status: 'active' },
          { label: 'IOCs Today', value: '156', status: 'warning' },
          { label: 'Feed Health', value: '98%', status: 'success' },
          { label: 'Update Frequency', value: '5min', status: 'info' }
        ].map((metric) => (
          <div key={metric.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{metric.label}</span>
              <div className={`h-2 w-2 rounded-full ${
                metric.status === 'active' ? 'bg-blue-500' :
                metric.status === 'warning' ? 'bg-yellow-500' :
                metric.status === 'success' ? 'bg-emerald-500' :
                'bg-gray-500'
              }`} />
            </div>
            <div className="text-2xl font-bold text-white">{metric.value}</div>
          </div>
        ))}
      </div>

      {/* Active Feeds */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Active Feeds</h2>
          <div className="flex items-center space-x-3">
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { name: 'Malware Database', type: 'Malware', iocs: 1245, status: 'active' },
            { name: 'Phishing Tracker', type: 'Phishing', iocs: 856, status: 'active' },
            { name: 'Botnet Monitor', type: 'Botnet', iocs: 423, status: 'warning' }
          ].map((feed, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className={`h-2 w-2 rounded-full ${
                  feed.status === 'active' ? 'bg-emerald-500' : 'bg-yellow-500'
                }`} />
                <div>
                  <div className="text-sm font-medium text-white">{feed.name}</div>
                  <div className="text-xs text-gray-400 mt-1">Type: {feed.type}</div>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-right">
                  <div className="text-sm font-medium text-white">{feed.iocs.toLocaleString()}</div>
                  <div className="text-xs text-gray-400">Total IOCs</div>
                </div>
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feed Statistics */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Feed Performance</h2>
          <div className="space-y-4">
            {[
              { metric: 'Average Response Time', value: '245ms', trend: 'down' },
              { metric: 'Update Success Rate', value: '99.9%', trend: 'up' },
              { metric: 'Daily IOC Volume', value: '2.4K', trend: 'up' }
            ].map((stat, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="text-sm text-gray-300">{stat.metric}</div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-white">{stat.value}</span>
                  <span className={`text-xs ${
                    stat.trend === 'up' ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {stat.trend === 'up' ? '↑' : '↓'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Updates</h2>
          <div className="space-y-4">
            {[
              { action: 'Feed Updated', feed: 'Malware Database', time: '5m ago' },
              { action: 'New IOCs Added', feed: 'Phishing Tracker', time: '10m ago' },
              { action: 'Feed Error', feed: 'Botnet Monitor', time: '15m ago' }
            ].map((update, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{update.action}</div>
                  <div className="text-xs text-gray-400 mt-1">{update.feed}</div>
                </div>
                <div className="text-xs text-gray-500">{update.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatFeeds;